/**
 * PermissionLine — A single schema/resource permission row.
 *
 * Shows: [schema icon] [name] [own badge] [restrictions] [verb icons]
 *
 * Used by: consent page, realm summary, EP summary, DLR preview.
 * In interactive mode, verb icons become toggles (pass onVerbClick).
 */
import { ChevronDown } from 'lucide-react';
import SchemaIcon from './common/SchemaIcon';
import VerbIcon from './VerbIcon';

export default function PermissionLine({
  schema,         // display name (e.g. "diary", "self", "health-metric")
  icon,           // schema icon name (for SchemaIcon component)
  verbs,          // [{ verb, variant, isOn?, disabled? }]
  isOwn,          // show "new entries only" badge (read-only display)
  isAllNodes,     // show "all nodes" indicator (no schema filter)
  fieldHint,      // { count, total, fieldNames }
  filterInfo,     // string summary
  filterDetail,   // [{ field, op, values }]
  onVerbClick,    // (verb) => void — makes verb icons interactive
  className,      // additional CSS classes
}) {
  return (
    <div className={`perm-line${className ? ` ${className}` : ''}`}>
      <div className="perm-line__left">
        <div className="perm-line__name-row">
          <span className="perm-schema-label">
            {icon && <SchemaIcon iconName={icon} size={12} className="perm-schema-label__icon" />}
            <span>{schema}</span>
          </span>
          {isOwn && (
            <span className="perm-own-badge" title="App can only modify entries it created — not your existing data">new entries only</span>
          )}
          {isAllNodes && (
            <span className="perm-all-badge" title="Applies to all nodes, not filtered by schema">all nodes</span>
          )}
        </div>
        {(fieldHint || filterInfo) && (
          <div className="perm-restrictions-list">
            {fieldHint && (
              <details className="perm-restriction">
                <summary className="perm-restriction__summary">
                  <ChevronDown size={9} className="perm-restriction__chevron" />
                  <span>specific fields only{fieldHint.total ? ` (${fieldHint.count}/${fieldHint.total})` : ''}</span>
                </summary>
                <div className="perm-restriction__detail">
                  {(fieldHint.fieldNames || []).map((f, fi) => (
                    <code key={fi} className="perm-field-tag">{f}</code>
                  ))}
                </div>
              </details>
            )}
            {filterInfo && (
              <details className="perm-restriction">
                <summary className="perm-restriction__summary">
                  <ChevronDown size={9} className="perm-restriction__chevron" />
                  <span>filtered</span>
                </summary>
                <div className="perm-restriction__detail">
                  {(filterDetail || []).map((f, fi) => (
                    <div key={fi} className="perm-filter-rule">
                      <code className="perm-field-tag">{f.field}</code>
                      <span className="perm-filter-rule__op">{f.op}</span>
                      {f.values.map((val, vi) => (
                        <code key={vi} className="perm-field-tag perm-field-tag--value">{val}</code>
                      ))}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>
      <span className="perm-line__icons">
        {verbs.map((v, j) => (
          <VerbIcon
            key={j}
            verb={v.verb}
            variant={v.variant}
            schemaName={schema}
            isOn={v.isOn !== false}
            disabled={v.disabled}
            onClick={onVerbClick ? () => onVerbClick(v.verb) : undefined}
          />
        ))}
      </span>
    </div>
  );
}
